// src/components/AddCompanyForm/LogoUploader.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import { useAuthContext } from "../../context/AuthContext";
import { useS3Upload } from "../../hooks/useS3Upload";
import { useTranslation } from "react-i18next";

export default function LogoUploader({ value, onChange, onBusyChange }) {
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  const { userData } = useAuthContext();
  const { uploadOne } = useS3Upload();
  const { t } = useTranslation("settings");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy]); // eslint-disable-line

  const onPick = () => inputRef.current?.click();

  const doUpload = async (file) => {
    try {
      if (!file) return;
      if (!/^image\//i.test(file.type))
        throw new Error(t("addCompanyForm.logo.imageTypeError"));
      if (file.size > 5 * 1024 * 1024) throw new Error(t("addCompanyForm.logo.sizeError"));
      setBusy(true);
      const prefix = `workspace-${userData?.id_workspace || "default"}/companies/${new Date().getFullYear()}`;
      const { url } = await uploadOne(file, { prefix });
      onChange?.(url);
    } catch (e) {
      console.error(e);
      alert(e?.message || t("addCompanyForm.logo.uploadFailed"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDrop = (e) => {
      prevent(e);
      const f = e.dataTransfer?.files?.[0];
      if (f) doUpload(f);
    };
    el.addEventListener("dragover", prevent);
    el.addEventListener("dragenter", prevent);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", prevent);
      el.removeEventListener("dragenter", prevent);
      el.removeEventListener("drop", onDrop);
    };
  }, []);

  return (
    <Box
      ref={dropRef}
      role="button"
      tabIndex={0}
      onClick={() => !busy && onPick()}
      onKeyDown={(e) => !busy && e.key === "Enter" && onPick()}
      sx={{
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        minHeight: 160,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 2,
        cursor: busy ? "default" : "pointer",
        bgcolor: value ? "background.default" : "transparent",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {!value ? (
        <Stack alignItems="center" spacing={1}>
          <ImageRoundedIcon color="action" sx={{ fontSize: 38 }} />
          <Typography variant="body2">{t("addCompanyForm.logo.dropHint")}</Typography>
          <Button size="small" startIcon={<UploadRoundedIcon />} variant="outlined" disabled={busy}>
            {busy ? t("shared.loading") : t("addCompanyForm.logo.chooseFile")}
          </Button>
        </Stack>
      ) : (
        <Stack alignItems="center" spacing={1} sx={{ width: "100%" }}>
          <img src={value} alt="Logo" style={{ maxHeight: 90, maxWidth: "100%", objectFit: "contain" }} />
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<UploadRoundedIcon />} variant="outlined" onClick={onPick} disabled={busy}>
              {t("addCompanyForm.logo.replace")}
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => onChange?.(null)}
              disabled={busy}
            >
              {t("addCompanyForm.logo.remove")}
            </Button>
          </Stack>
        </Stack>
      )}
      <input
        type="file"
        ref={inputRef}
        hidden
        accept="image/png,image/jpeg"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.currentTarget.value = "";
          if (f) doUpload(f);
        }}
      />
    </Box>
  );
}
